import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, TrendingUp, ArrowLeft, BookOpen } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { CHAPTERS } from './evolucion/chaptersConfig';
import ChapterCard from './evolucion/ChapterCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EvolucionDashboard() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally { setLoading(false); }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    const completedModules = profile?.modules_completed || [];
    const isModuleCompleted = (id) => completedModules.some(m => m.module_id === id);

    const totalModules = CHAPTERS.reduce((acc, ch) => acc + ch.moduleIds.length, 0);
    const totalCompleted = CHAPTERS.reduce((acc, ch) => acc + ch.moduleIds.filter(id => isModuleCompleted(id)).length, 0);
    const globalPct = Math.round((totalCompleted / totalModules) * 100);

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div className="flex items-center gap-3">
                                <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                                <div>
                                    <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']" data-testid="evolucion-title">Mi evolución</h1>
                                    <p className="text-sm text-[#4A5B63]">Progreso integrado SENDA</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/download`} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="border-[#5FAF9D]/30 text-[#5FAF9D] hover:bg-[#5FAF9D]/5 px-2 sm:px-4" data-testid="manual-link">
                                    <BookOpen className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Manual</span>
                                </Button>
                            </a>
                            <Link to="/usuario"><Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">Volver</Button></Link>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
                {/* Global progress bar */}
                <div className="mb-10">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-[#5FAF9D]" />
                            <span className="text-sm font-semibold text-[#3E5F6E] uppercase tracking-wide">Progreso global</span>
                        </div>
                        <span className="text-2xl font-bold text-[#3E5F6E] font-['Outfit']">{globalPct}%</span>
                    </div>
                    <div className="h-3 bg-[#DCE3E8] rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#3E5F6E] via-[#5FAF9D] to-[#4A9A89] rounded-full transition-all duration-1000" style={{ width: `${globalPct}%` }} />
                    </div>
                    <p className="text-xs text-[#7A8A93] mt-2">{totalCompleted} de {totalModules} módulos completados</p>
                </div>

                {/* Timeline */}
                <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#3E5F6E] via-[#5FAF9D] to-[#DCE3E8] hidden lg:block" />

                    <div className="space-y-8">
                        {CHAPTERS.map((chapter, chIdx) => (
                            <ChapterCard
                                key={chapter.num}
                                chapter={chapter}
                                chIdx={chIdx}
                                isModuleCompleted={isModuleCompleted}
                                profile={profile}
                            />
                        ))}
                    </div>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
